/**
 * ChurchPropertiesTab — buildings and facilities for a Campus (an Assembly
 * that owns property). Facilities past their next service date are flagged.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Building2, Plus, Trash2, Pencil, Wrench, AlertTriangle, Loader2, Landmark,
  CalendarClock, MailWarning, Send,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const TYPE_LABELS = {
  sanctuary: 'Sanctuary', hall: 'Hall', classroom: 'Classroom',
  office: 'Office', accommodation: 'Accommodation', other: 'Other',
};
const STATUS_STYLES = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  under_maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
  closed: 'bg-rose-50 text-rose-700 border-rose-200',
};
const CONDITION_STYLES = {
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  fair: 'bg-amber-50 text-amber-700 border-amber-200',
  poor: 'bg-rose-50 text-rose-700 border-rose-200',
};

const Select = ({ value, onChange, options, testId }) => (
  <select value={value} onChange={onChange} data-testid={testId}
          className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
    {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
  </select>
);

const BuildingDialog = ({ churchId, building, onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: building?.name || '', building_type: building?.building_type || 'sanctuary',
    capacity: building?.capacity || 0, status: building?.status || 'active',
    notes: building?.notes || '',
  });
  const save = useMutation({
    mutationFn: () => (building
      ? api(`/api/church/${churchId}/buildings/${building.id}`, { method: 'PUT', body: JSON.stringify(form) })
      : api(`/api/church/${churchId}/buildings`, { method: 'POST', body: JSON.stringify(form) })),
    onSuccess: () => {
      toast.success(building ? 'Building updated' : 'Building added');
      qc.invalidateQueries({ queryKey: ['church-buildings', churchId] });
      qc.invalidateQueries({ queryKey: ['church-properties-summary', churchId] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="building-dialog">
        <DialogHeader>
          <DialogTitle>{building ? `Edit ${building.name}` : 'Add a building'}</DialogTitle>
          <DialogDescription>Buildings hold the facilities you maintain.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label>
            <Input value={form.name} autoFocus data-testid="building-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Main Sanctuary" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.building_type} testId="building-type-select"
                      onChange={(e) => setForm({ ...form, building_type: e.target.value })}
                      options={Object.entries(TYPE_LABELS)} />
            </div>
            <div><Label>Capacity</Label>
              <Input type="number" min={0} value={form.capacity} data-testid="building-capacity-input"
                     onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label>
              <Select value={form.status} testId="building-status-select"
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      options={[['active', 'Active'], ['under_maintenance', 'Under Maintenance'], ['closed', 'Closed']]} />
            </div>
            <div><Label>Notes</Label>
              <Input value={form.notes} data-testid="building-notes-input"
                     onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || form.name.trim().length < 2}
                  data-testid="building-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FacilityDialog = ({ churchId, building, facility, onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: facility?.name || '', condition: facility?.condition || 'good',
    last_maintenance_date: facility?.last_maintenance_date || '',
    next_maintenance_date: facility?.next_maintenance_date || '',
    reminder_email: facility?.reminder_email || '',
    notes: facility?.notes || '',
  });
  const save = useMutation({
    mutationFn: () => (facility
      ? api(`/api/church/${churchId}/facilities/${facility.id}`, { method: 'PUT', body: JSON.stringify(form) })
      : api(`/api/church/${churchId}/buildings/${building.id}/facilities`, { method: 'POST', body: JSON.stringify(form) })),
    onSuccess: () => {
      toast.success(facility ? 'Facility updated' : 'Facility added');
      qc.invalidateQueries({ queryKey: ['church-buildings', churchId] });
      qc.invalidateQueries({ queryKey: ['church-properties-summary', churchId] });
      qc.invalidateQueries({ queryKey: ['church-maintenance-upcoming', churchId] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="facility-dialog">
        <DialogHeader>
          <DialogTitle>{facility ? `Edit ${facility.name}` : `Add a facility to ${building.name}`}</DialogTitle>
          <DialogDescription>E.g. Kitchen, Toilets, Parking, Generator, CCTV.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label>
            <Input value={form.name} autoFocus data-testid="facility-name-input"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Generator" />
          </div>
          <div><Label>Condition</Label>
            <Select value={form.condition} testId="facility-condition-select"
                    onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    options={[['good', 'Good'], ['fair', 'Fair'], ['poor', 'Poor']]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Last maintenance</Label>
              <Input type="date" value={form.last_maintenance_date} data-testid="facility-last-date-input"
                     onChange={(e) => setForm({ ...form, last_maintenance_date: e.target.value })} />
            </div>
            <div><Label>Next scheduled</Label>
              <Input type="date" value={form.next_maintenance_date} data-testid="facility-next-date-input"
                     onChange={(e) => setForm({ ...form, next_maintenance_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Reminder email <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input type="email" value={form.reminder_email} data-testid="facility-reminder-email-input"
                   placeholder="Leave blank to use the campus admins"
                   onChange={(e) => setForm({ ...form, reminder_email: e.target.value })} />
            <p className="mt-1 text-[11px] text-slate-400">
              We email a week before the next service date. Campus admins are always copied.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || form.name.trim().length < 2}
                  data-testid="facility-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Upcoming servicing — the in-app twin of the reminder emails. */
const MaintenanceRemindersCard = ({ churchId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['church-maintenance-upcoming', churchId],
    queryFn: () => api(`/api/church/${churchId}/properties/maintenance/upcoming?days=60`),
  });
  const remind = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/facilities/${id}/remind`, { method: 'POST' }),
    onSuccess: (res) => {
      if (res.status === 'sent') toast.success(`Reminder emailed to ${res.delivered_to.length} recipient(s)`);
      else if (res.status === 'no_recipients') toast.error('No campus admin has an email address on file');
      else toast.message('Reminder queued', { description: 'It will send once email is switched on in System Settings.' });
      qc.invalidateQueries({ queryKey: ['church-maintenance-upcoming', churchId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const items = data?.items || [];

  return (
    <Card data-testid="maintenance-reminders-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-amber-600" /> Upcoming maintenance
          {data?.overdue > 0 && (
            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]"
                   data-testid="maintenance-overdue-count">
              {data.overdue} overdue
            </Badge>
          )}
          {data?.due_soon > 0 && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                   data-testid="maintenance-due-soon-count">
              {data.due_soon} due this week
            </Badge>
          )}
        </CardTitle>
        <p className="text-[11px] text-slate-400">
          Reminders go out {data?.lead_days ?? 7} days ahead ·{' '}
          <Link to="/admin/maintenance/calendar" className="font-medium text-indigo-600 hover:underline"
                data-testid="maintenance-open-calendar">
            open calendar
          </Link>
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {data && !data.email_enabled && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500"
             data-testid="maintenance-email-disabled">
            <MailWarning className="mr-1 inline h-3.5 w-3.5 text-slate-400" />
            Email is switched off — reminders are queued and will send once SMTP is set in System Settings.
          </p>
        )}
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="maintenance-empty">
            Nothing due in the next 60 days. Add a next service date to a facility to be reminded.
          </p>
        ) : items.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2"
               data-testid={`maintenance-row-${f.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-800">
                {f.name}
                <span className="ml-1.5 text-xs font-normal text-slate-400">{f.building}</span>
              </p>
              <p className="text-[11px] text-slate-500">
                due {f.next_maintenance_date} ·{' '}
                <span className={f.is_overdue ? 'font-semibold text-rose-600' : f.due_soon ? 'font-semibold text-amber-600' : ''}>
                  {f.is_overdue ? `${Math.abs(f.days_until)} day(s) overdue`
                    : f.days_until === 0 ? 'due today' : `in ${f.days_until} day(s)`}
                </span>
                {f.last_reminder?.status === 'sent' && ' · reminder sent'}
                {f.last_reminder?.status === 'queued' && ' · reminder queued'}
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={remind.isPending}
                    onClick={() => remind.mutate(f.id)}
                    data-testid={`maintenance-remind-${f.id}`}>
              <Send className="mr-1 h-3.5 w-3.5" /> Remind now
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default function ChurchPropertiesTab({ churchId }) {
  const qc = useQueryClient();
  const [buildingDialog, setBuildingDialog] = useState(null);   // {} = new, {building} = edit
  const [facilityDialog, setFacilityDialog] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['church-buildings', churchId],
    queryFn: () => api(`/api/church/${churchId}/buildings`),
  });
  const { data: summary } = useQuery({
    queryKey: ['church-properties-summary', churchId],
    queryFn: () => api(`/api/church/${churchId}/properties/summary`),
  });
  const buildings = data?.items || [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['church-buildings', churchId] });
    qc.invalidateQueries({ queryKey: ['church-properties-summary', churchId] });
  };
  const delBuilding = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/buildings/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Building removed'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const delFacility = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/facilities/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Facility removed'); invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4" data-testid="church-properties-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Buildings', summary?.buildings], ['Facilities', summary?.facilities],
          ['Seats / beds', summary?.capacity], ['Poor condition', summary?.poor_condition]].map(([label, n]) => (
          <Card key={label} data-testid={`properties-stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
            <CardContent className="pt-4 pb-3">
              <Landmark className="h-3.5 w-3.5 text-slate-400" />
              <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">{n ?? 0}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {(summary?.maintenance_overdue || []).length > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="properties-overdue-panel">
          <CardContent className="pt-4">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              {summary.maintenance_overdue.length} facilit{summary.maintenance_overdue.length === 1 ? 'y' : 'ies'} past due for maintenance
            </p>
            <ul className="mt-2 space-y-1 text-xs text-amber-800">
              {summary.maintenance_overdue.map((f) => (
                <li key={f.id} data-testid={`properties-overdue-${f.id}`}>
                  {f.name} · {f.building} — due {f.next_maintenance_date}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <MaintenanceRemindersCard churchId={churchId} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-indigo-600" /> Buildings ({buildings.length})
          </CardTitle>
          <Button size="sm" onClick={() => setBuildingDialog({})} data-testid="add-building-btn">
            <Plus className="h-4 w-4 mr-1" /> Add building
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? <Skeleton className="h-24 w-full" /> : buildings.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="buildings-empty">
              No buildings listed yet. Add your sanctuary, halls, classrooms, offices and accommodation.
            </p>
          ) : buildings.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 p-3"
                 data-testid={`building-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-medium text-slate-800">
                    {b.name}
                    <Badge variant="outline" className="ml-2 text-[10px]">{TYPE_LABELS[b.building_type]}</Badge>
                    <Badge variant="outline" className={`ml-1 text-[10px] ${STATUS_STYLES[b.status]}`}
                           data-testid={`building-status-${b.id}`}>
                      {b.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400">
                    capacity {b.capacity || 0} · {b.facility_count} facilit{b.facility_count === 1 ? 'y' : 'ies'}
                    {b.overdue_count > 0 && ` · ${b.overdue_count} overdue`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setFacilityDialog({ building: b })}
                          data-testid={`add-facility-${b.id}`}>
                    <Wrench className="h-3.5 w-3.5 mr-1" /> Add facility
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8"
                          onClick={() => setBuildingDialog({ building: b })}
                          data-testid={`building-edit-${b.id}`}>
                    <Pencil className="h-3.5 w-3.5 text-slate-400" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8"
                          onClick={() => { if (window.confirm(`Remove ${b.name} and its facilities?`)) delBuilding.mutate(b.id); }}
                          data-testid={`building-delete-${b.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                  </Button>
                </div>
              </div>

              {b.facilities.length > 0 && (
                <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
                  {b.facilities.map((f) => (
                    <div key={f.id} className="flex items-center justify-between gap-2 py-2 text-sm"
                         data-testid={`facility-row-${f.id}`}>
                      <span className="text-slate-700">
                        {f.name}
                        <Badge variant="outline" className={`ml-2 text-[10px] ${CONDITION_STYLES[f.condition]}`}>
                          {f.condition}
                        </Badge>
                        {f.maintenance_overdue && (
                          <Badge variant="outline" className="ml-1 text-[10px] bg-rose-50 text-rose-700 border-rose-200"
                                 data-testid={`facility-overdue-${f.id}`}>
                            overdue
                          </Badge>
                        )}
                      </span>
                      <span className="flex items-center gap-2 text-[11px] text-slate-400">
                        {f.last_maintenance_date && `last ${f.last_maintenance_date}`}
                        {f.next_maintenance_date && ` · next ${f.next_maintenance_date}`}
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => setFacilityDialog({ building: b, facility: f })}
                                data-testid={`facility-edit-${f.id}`}>
                          <Pencil className="h-3 w-3 text-slate-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => delFacility.mutate(f.id)}
                                data-testid={`facility-delete-${f.id}`}>
                          <Trash2 className="h-3 w-3 text-rose-500" />
                        </Button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {buildingDialog && (
        <BuildingDialog churchId={churchId} building={buildingDialog.building}
                        onClose={() => setBuildingDialog(null)} />
      )}
      {facilityDialog && (
        <FacilityDialog churchId={churchId} building={facilityDialog.building}
                        facility={facilityDialog.facility}
                        onClose={() => setFacilityDialog(null)} />
      )}
    </div>
  );
}
